'use client'
import { ethers, providers } from "ethers"

import WalletConnectProvider from "../lib/walletConnectV2"

import { useCallback, useEffect, useReducer, useRef, useState } from "react"
import Web3Modal from "web3modal"
import { useSetAtom } from "jotai"
import { isWeb3AuthenticatedAtom } from "../../services/fashion-index/AuthServices"
import useRequest from "./useRequest"
import useAuth from "./useAuth"

const WALLET_DISCONNECTED_KEY = "hape_wallet_disconnected"

const wasDisconnectedByUser = () => {
	try {
		return window.sessionStorage.getItem(WALLET_DISCONNECTED_KEY) === "1"
	} catch {
		return false
	}
}

const markDisconnectedByUser = () => {
	try {
		window.sessionStorage.setItem(WALLET_DISCONNECTED_KEY, "1")
	} catch {
		// sessionStorage can be blocked; in-memory flag still covers this tab.
	}
}

const clearDisconnectedByUser = () => {
	try {
		window.sessionStorage.removeItem(WALLET_DISCONNECTED_KEY)
	} catch {
		// Ignore storage failures.
	}
}


const INFURA_ID = process.env.NEXT_PUBLIC_INFURA_ID || "a6270a3a3d6c43f78ae8f99b86c16504"
const providerOptions = {
	walletconnect: {
		package: WalletConnectProvider, // required
		options: {
			infuraId: INFURA_ID, // required
			// qrcode: false,
			qrcode: true,
			qrcodeModalOptions: {
				desktopLinks: ["metamask", "rainbow", "trust", "argent", "imtoken", "pillar"],
				mobileLinks: ["metamask", "rainbow", "trust", "argent", "imtoken", "pillar"],
			},
		},
	},
}

let web3Modal
if (typeof window !== "undefined") {
	web3Modal = new Web3Modal({
		network: "mainnet", // optional
		cacheProvider: true,
		providerOptions, // required
	})
}

const isMetaMaskUnavailableError = (error: unknown) => {
	const message = String(
		(error as any)?.message || (error as any)?.reason || error || "",
	)
	return (
		message.includes("Failed to connect to MetaMask") ||
		message.includes("MetaMask extension not found")
	)
}

const isUserCancelledConnect = (error: unknown) => {
	const message = String(
		(error as any)?.message || (error as any)?.reason || error || "",
	)
	const code = (error as any)?.code
	return (
		code === 4001 ||
		message.includes("Modal closed by user") ||
		message.includes("User rejected") ||
		message.includes("User denied")
	)
}

const clearWalletCache = () => {
	try {
		web3Modal?.clearCachedProvider()
		window.localStorage.removeItem("WEB3_CONNECT_CACHED_PROVIDER")
		window.localStorage.removeItem("walletconnect")
	} catch {
		// Ignore storage failures; connect can still proceed without cache.
	}
}

if (typeof window !== "undefined") {
	window.addEventListener("unhandledrejection", (event) => {
		if (!isMetaMaskUnavailableError(event.reason)) return
		event.preventDefault()
		clearWalletCache()
	})
}

type StateType = {
	provider?: any
	web3Provider?: any
	address?: string
	chainId?: number
}

type ActionType =
	| {
			type: "SET_WEB3_PROVIDER"
			provider?: StateType["provider"]
			web3Provider?: StateType["web3Provider"]
			address?: StateType["address"]
			chainId?: StateType["chainId"]
	  }
	| {
			type: "SET_ADDRESS"
			address?: StateType["address"]
	  }
	| {
			type: "SET_CHAIN_ID"
			chainId?: StateType["chainId"]
	  }
	| {
			type: "RESET_WEB3_PROVIDER"
	  }

const initialState: StateType = {
	provider: null,
	web3Provider: null,
	address: null,
	chainId: null,
}

function reducer(state: StateType, action: ActionType): StateType {
	switch (action.type) {
		case "SET_WEB3_PROVIDER":
			return {
				...state,
				provider: action.provider,
				web3Provider: action.web3Provider,
				address: action.address,
				chainId: action.chainId,
			}
		case "SET_ADDRESS":
			return {
				...state,
				address: action.address,
			}
		case "SET_CHAIN_ID":
			return {
				...state,
				chainId: action.chainId,
			}
		case "RESET_WEB3_PROVIDER":
			return initialState
		default:
			throw new Error()
	}
}

function useWallet() {
	const [equipment, setEquipment] = useState(null)
	const [dynamicEquipment, setDynamicEquipment] = useState(null)
	const [equipmentSignature, setEquipmentSignature] = useState(null)

	const [account, setAccount] = useState<string>(null)
	const [connection, setConnection] = useState(null)
	const [isSigning, setIsSigning] = useState(false)
	const [hasSigned, setHasSigned] = useState(false)
	const [isGettingEquipment, setIsGettingEquipment] = useState(false)
	const [isAddressOwnerOf, setIsAddressOwnerOf] = useState(false)
	const [isCheckingOwnerOf, setIsCheckingOwnerOf] = useState(false)
	const setIsWeb3Authenticated = useSetAtom(isWeb3AuthenticatedAtom)
	const { checkWallet, bindWallet } = useRequest()
	const { get_access_token } = useAuth()
	const disconnectingRef = useRef(false)
	const userDisconnectedRef = useRef(false)
	const triedBindRef = useRef(false)

	const rememberUserDisconnect = () => {
		userDisconnectedRef.current = true
		markDisconnectedByUser()
	}

	const forgetUserDisconnect = () => {
		userDisconnectedRef.current = false
		clearDisconnectedByUser()
	}

	const ensureWalletBound = async (address: string, signer: ethers.Signer) => {
		if (triedBindRef.current) return
		if (await checkWallet(address)) return
		triedBindRef.current = true
		try {
			await bindWallet(address, signer)
		} catch (error) {
			console.warn("Wallet bind skipped:", error)
		}
	}


	function unSign() {
		setHasSigned(false)
		setIsGettingEquipment(false)
		setEquipmentSignature(null)
		setEquipment(null)
	}

	const [state, dispatch] = useReducer(reducer, initialState)
	const { provider, web3Provider } = state

	const connect = useCallback(async function () {
		if (!web3Modal) return

		try {
			forgetUserDisconnect()
			triedBindRef.current = false
			// Stale cached "injected" skips the picker and hangs. Always show it.
			clearWalletCache()
			const provider = await web3Modal.connect()
			const web3Provider = new providers.Web3Provider(provider)
			const signer = web3Provider.getSigner()
			const address = await signer.getAddress()

			const network = await web3Provider.getNetwork()
			setConnection(provider)

			// Bind before exposing the account so profile points can load.
			await ensureWalletBound(address, signer)
			setAccount(address)

			dispatch({
				type: "SET_WEB3_PROVIDER",
				provider,
				web3Provider,
				address,
				chainId: network.chainId,
			})
		} catch (error) {
			if (isUserCancelledConnect(error) || isMetaMaskUnavailableError(error)) {
				console.warn("Wallet connect skipped:", error)
				return
			}
			console.error("Wallet connect failed:", error)
		}
	}, [])

	// Reconnect only if this origin already has an approved account.
	const autoConnect = async () => {
		if (userDisconnectedRef.current || wasDisconnectedByUser()) return
		const access_token = await get_access_token()
		if (
			typeof window === "undefined" ||
			provider ||
			web3Provider ||
			!access_token
		) {
			return
		}
		const ethereum = (window as any).ethereum
		if (!ethereum?.request) return
		try {
			const accounts = await ethereum.request({ method: "eth_accounts" })
			if (!Array.isArray(accounts) || accounts.length === 0) return

			const web3Provider = new providers.Web3Provider(ethereum)
			const signer = web3Provider.getSigner()
			const address = await signer.getAddress()
			const network = await web3Provider.getNetwork()
			setConnection(ethereum)

			await ensureWalletBound(address, signer)
			setAccount(address)
			dispatch({
				type: "SET_WEB3_PROVIDER",
				provider: ethereum,
				web3Provider,
				address,
				chainId: network.chainId,
			})
		} catch (error) {
			console.warn("Auto-connect failed:", error)
		}
	}
	useEffect(() => {
		autoConnect()
	}, [provider, web3Provider, connect])

	const checkOwnerOf = async (hapeTokenId) => {
		setIsCheckingOwnerOf(true)
		if (process.env.NEXT_PUBLIC_IS_TESTING === "false") {
			setIsAddressOwnerOf(true)
		} else {
			setIsAddressOwnerOf(true)
		}

		setIsCheckingOwnerOf(false)
	}

	const resetWeb3AuthenticationCredentials = () => {
		setIsWeb3Authenticated(false)
	}

	const disconnect = useCallback(
		async function () {
			if (disconnectingRef.current) return
			disconnectingRef.current = true
			rememberUserDisconnect()
			triedBindRef.current = false

			setAccount("")
			setEquipment(null)
			setIsGettingEquipment(false)
			setIsSigning(false)
			setHasSigned(false)

			resetWeb3AuthenticationCredentials()

			await web3Modal.clearCachedProvider()
			if (provider?.disconnect && typeof provider.disconnect === "function") {
				console.log("provider.disconnect", provider.disconnect)
				await provider.disconnect()
			}
			if (provider?.close && typeof provider.close === "function") {
				console.log("provider.close", provider.close)
				await provider.close()
			}
			// Ask MetaMask to drop this site so eth_accounts stays empty.
			const injected = provider || (typeof window !== "undefined" ? (window as any).ethereum : null)
			if (injected?.request) {
				try {
					await injected.request({
						method: "wallet_revokePermissions",
						params: [{ eth_accounts: {} }],
					})
				} catch {
					// Older wallets and WalletConnect may not support revoke.
				}
			}
			try {
				console.log('removeItem walletconnect')
				window.localStorage.removeItem("walletconnect")
				window.localStorage.removeItem("WEB3_CONNECT_CACHED_PROVIDER")
				Object.keys(window.localStorage)
					.filter((key) => key.startsWith("wc@2:"))
					.forEach((key) => window.localStorage.removeItem(key))
			  } catch (e) { console.log('removeItem walletconnect error', e) }
			dispatch({
				type: "RESET_WEB3_PROVIDER",
			})
			disconnectingRef.current = false
		},
		[provider],
	)

	// A `provider` should come with EIP-1193 events. We'll listen for those events
	// here so that when a user switches accounts or networks, we can update the
	// local React state with that new information.
	useEffect(() => {
		// console.log('useEffect provider', provider?.on)
		if (provider?.on) {
			// console.log('useEffect provider?.on')
			const handleAccountsChanged = async (accounts) => {
				// eslint-disable-next-line no-console
				console.log("accountsChanged", accounts)

				resetWeb3AuthenticationCredentials()
				if (!accounts || accounts.length === 0) {
					await disconnect()
					return
				}
				let account = accounts[0]
				if (account) {
					forgetUserDisconnect()
					triedBindRef.current = false
					const web3Provider = new providers.Web3Provider(provider)
					const signer = web3Provider.getSigner()
					await ensureWalletBound(account, signer)
					setAccount(account)
					dispatch({
						type: "SET_ADDRESS",
						address: account,
					})
				}
				
			}

			// https://docs.ethers.io/v5/concepts/best-practices/#best-practices--network-changes
			const handleChainChanged = (_hexChainId) => {
				resetWeb3AuthenticationCredentials()

				window.location.reload()
			}

			const handleDisconnect = (error) => {
				disconnect()
			}

			const handleConnect = (accounts) => {
				console.log('handleConnect', accounts)
				// console.log("accountsChanged", accounts)

				// resetWeb3AuthenticationCredentials()

				// setAccount(accounts[0])
				// dispatch({
				// 	type: "SET_ADDRESS",
				// 	address: accounts[0],
				// })
			}

			provider.on("accountsChanged", handleAccountsChanged)
			provider.on("chainChanged", handleChainChanged)
			provider.on("disconnect", handleDisconnect)
			provider.on("connect", handleConnect)
			// Subscription Cleanup
			return () => {
				if (provider.removeListener) {
					provider.removeListener("accountsChanged", handleAccountsChanged)
					provider.removeListener("chainChanged", handleChainChanged)
					provider.removeListener("disconnect", handleDisconnect)
					provider.removeListener("connect", handleConnect)
				}
			}
		}
	}, [provider, disconnect])

	const getFormattedAddress = () => {
		if (account) {
			return (
				account.substring(0, 6) +
				"..." +
				account.substring(account.length - 4, account.length)
			)
		}
	}

	return {
		web3Provider,
		equipment,
		equipmentSignature,
		connect,
		disconnect,
		getFormattedAddress,
		checkOwnerOf,
		isSigning,
		hasSigned,
		unSign,
		isGettingEquipment,
		isAddressOwnerOf,
		isCheckingOwnerOf,
		account,
		dynamicEquipment,
	}
}

export default useWallet
